import { AxiosInstance } from 'axios';
import { defineApi, useProjectSpecificApiClient } from '../../apiForm/api';
import { isSuccessfulOrCustomResponse } from '../../utils/statusValidator';
import convertNDPricesToNDPriceUpdates from './priceUtils';

const copyImage = async (
  projectSpecificClient: AxiosInstance,
  oldRef: string,
): Promise<string> => {
  // download coupon image blob through frontend-resources
  const fullURL = `/frontend-resources/images?ref=${oldRef}`;
  const { data: blob } = await projectSpecificClient.get(fullURL, {
    responseType: 'blob',
  });
  // convert blob to file
  const file = new File([blob], 'coupon-image');
  // create a mulitform request with the image file
  const formData = new FormData();
  formData.append('file', file);
  // reupload image to coupon endpoint under the current project
  const {
    data: { ref: newRef },
  } = await projectSpecificClient.post('/coupons/images', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return newRef;
};

const copyCoupon = async (
  projectSpecificClient: AxiosInstance,
  coupon: Record<string, any>,
): Promise<void> => {
  const jsonData = coupon;
  jsonData.projectID = undefined;

  // copy images to new project if needed
  if (jsonData.translations?.image?.de) {
    jsonData.translations.image.de = await copyImage(
      projectSpecificClient,
      jsonData.translations.image.de,
    );
  }
  if (jsonData.translations?.image?.en) {
    jsonData.translations.image.en = await copyImage(
      projectSpecificClient,
      jsonData.translations.image.en,
    );
  }

  // create coupon, expect 409 if coupon already exists
  const { status } = await projectSpecificClient.post('/coupons', jsonData, {
    headers: {
      'Content-Type': 'application/json',
    },
    validateStatus: (statusCode) =>
      isSuccessfulOrCustomResponse(statusCode, [409]),
  });

  // if coupon exists, get current etag and update
  if (status === 409) {
    const {
      headers: { etag },
    } = await projectSpecificClient.get(`/coupons/${jsonData.id}`);
    await projectSpecificClient.put(`/coupons/${jsonData.id}`, jsonData, {
      headers: {
        'Content-Type': 'application/json',
        'If-Match': etag,
      },
    });
  }
};

const useApi = defineApi({
  getShops: async (client) =>
    client
      .get('/shops', {
        headers: {
          Accept: 'application/json',
        },
      })
      .then((resp) => resp.data?.shops || resp.data || []),
  getProducts: async (client) =>
    client
      .get('/products', {
        params: { limit: 0 },
        headers: {
          Accept: 'application/vnd.snabble.product.v2+x-ndjson',
        },
        transformResponse: (res: any) => res,
        responseType: 'json',
      })
      .then((resp) => resp.data),
  uploadProducts: async (client, data) =>
    client
      .post('/products/_batch', data, {
        headers: {
          'Content-Type': 'application/vnd.snabble.product.v2+x-ndjson',
        },
        transformResponse: (res: any) => res,
        responseType: 'json',
      })
      .then((resp) => resp.data),
  deleteProducts: async (client) =>
    client
      .delete('/products', {
        transformResponse: (res: any) => res,
        responseType: 'json',
      })
      .then((resp) => resp.data),
  getPrices: async (client) =>
    client
      .get('/pricing/products', {
        params: { limit: 0 },
        headers: {
          Accept: 'application/vnd.snabble.pricing.update+x-ndjson',
        },
        transformResponse: (res: any) => res,
        responseType: 'json',
      })
      .then((resp) => resp.data),
  uploadPrices: async (client, data) =>
    // NOTE: having to wrap all prices might be problematic for big datasets
    // as JS is not that performant when it comes to editing big arrays in memory
    client
      .post('/pricing/products/_batch', convertNDPricesToNDPriceUpdates(data), {
        headers: {
          'Content-Type': 'application/vnd.snabble.pricing.update+x-ndjson',
        },
        transformResponse: (res: any) => res,
        responseType: 'json',
      })
      .then((resp) => resp.data),
  deletePrices: async (client) =>
    client
      .delete('/pricing/products', {
        transformResponse: (res: any) => res,
        responseType: 'json',
      })
      .then((resp) => resp.data),
  getProductMenus: async (client) =>
    client
      .get('/checkout-devices/categories', {
        params: { limit: 0 },
        headers: {
          Accept: 'application/json',
        },
        transformResponse: (res: any) => res,
        responseType: 'json',
        validateStatus: (statusCode) =>
          isSuccessfulOrCustomResponse(statusCode, [404]),
      })
      .then((resp) => (resp.status !== 404 ? resp.data : '{}')),
  uploadProductMenus: async (client, data) =>
    client
      .put('/checkout-devices/categories', data, {
        headers: {
          'Content-Type': 'application/json',
        },
        transformResponse: (res: any) => res,
        responseType: 'json',
      })
      .then((resp) => resp.data),
  getPromotions: async (client) =>
    client
      .get('/promotions/configurations/active', {
        headers: {
          Accept: 'application/json',
        },
        transformResponse: (res: any) => res,
        responseType: 'json',
        validateStatus: (statusCode) =>
          isSuccessfulOrCustomResponse(statusCode, [404]),
      })
      .then((resp) => (resp.status !== 404 ? resp.data : '{}')),
  uploadPromotions: async (client, data) => {
    const { headers } = await client.post(
      '/promotions/configurations',
      data,
      {},
    );
    // location is the config URL with a leading projectID. Here we only grab the numeric id at the
    // end of that URL to place into the activation URL
    const configId = (headers.location || '').match(/(?<=.*)\d+$/)?.[0] || null;
    if (configId === null) return;
    await client.post(`/promotions/configurations/id/${configId}/activate`);
  },
  triggerThirdPartyExport: async (client, shopId: string) =>
    client.post(`/products/events/shop/${shopId}`, undefined, {
      headers: {
        Accept: 'application/json',
      },
    }),
  syncTransactionsStatistics: async (
    client,
    { from, to }: { from: string; to: string },
  ) =>
    client.post('/retailer/transactions/statistics/backfill', undefined, {
      params: {
        from: new Date(from).toISOString(),
        to: new Date(to).toISOString(),
      },
    }),
  getCoupons: async (client) =>
    client
      .get('/coupons', {
        headers: {
          Accept: 'application/json',
        },
        transformResponse: (res: any) => res,
        responseType: 'json',
      })
      .then((resp) => JSON.stringify(JSON.parse(resp.data)?.coupons)),
  uploadCoupons: async (client, data) => {
    // NOTE copying coupons involves a lot of steps (downloading and reuploading possible
    // images, creating a new coupon and if the coupon already exists getting an etag and
    // modifying the existing one). Each step itself is trivial but having up to seven
    // steps per coupon is very error prone. For this reason this function should only be
    // used for demo projects and not on production.

    // mapping the copy function to promises to be able to wait until all of the are finished
    await Promise.all(
      JSON.parse(data).map(async (coupon: any) => copyCoupon(client, coupon)),
    );
  },
});

export default function useProductsApi() {
  const client = useProjectSpecificApiClient();
  return useApi(client);
}
